import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Sparkles, ArrowRight, ArrowLeft, CheckCircle, Settings2, Zap, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProducts } from "@/hooks/useProducts";
import { useProductSpecBySlug } from "@/hooks/useProductSpecs";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { generateFirewallPdf } from "@/utils/generateFirewallPdf";

interface FirewallRequirements {
  users: number;
  vpnConnections: number;
  redundancy: boolean;
  budget: "low" | "medium" | "high";
  firewallThroughput: number;
  ipsThroughput: number;
}

interface AdvancedFirewallRequirements {
  // Performance
  firewallThroughputUdp: number;
  firewallThroughputImix: number;
  ipsThroughputUdp: number;
  ipsThroughputImix: number;
  vpnThroughputAes: number;
  threatPreventionThroughput: number;
  
  // Capacity
  users: number;
  ngfwUsers: number;
  concurrentConnections: number;
  newConnectionsPerSecond: number;
  
  // VPN
  vpnIpsecTunnels: number;
  vpnSslTunnels: number;
  
  // Network
  maxRules: number;
  maxStaticRoutes: number;
  maxQosQueues: number;
  maxLogicalInterfaces: number;
  
  // Features (checkboxes)
  highAvailability: boolean;
  redundantPowerSupply: boolean;
  redundantDisk: boolean;
  tpmChipset: boolean;
  
  // Interfaces (checkboxes)
  needsCopper1g: boolean;
  needsCopper2_5g: boolean;
  needsFiber1g: boolean;
  needsFiber10g: boolean;
  needsFiber25g: boolean;
  needsFiber40g: boolean;
  
  // Industrial (checkboxes)
  dinRail: boolean;
  fanLess: boolean;
  industrialProtocols: boolean;
}

const translations = {
  nl: {
    title: "Firewall Advisor",
    triggerButton: "Welke firewall past bij mij?",
    step1Title: "Hoeveel gebruikers/verbindingen?",
    step2Title: "VPN verbindingen nodig?",
    step3Title: "Performance eisen",
    step4Title: "Extra opties",
    users: "Aantal gebruikers",
    vpnConnections: "VPN verbindingen",
    redundancy: "High Availability (redundantie)",
    redundancyDesc: "Twee firewalls in cluster voor maximale uptime",
    budget: "Budget indicatie",
    budgetLow: "Budget-vriendelijk",
    budgetMedium: "Gemiddeld",
    budgetHigh: "Premium",
    firewallThroughput: "Firewall throughput (Gbps)",
    ipsThroughput: "IPS throughput (Gbps)",
    next: "Volgende",
    back: "Terug",
    getAdvice: "Advies krijgen",
    loading: "AI analyseert je eisen...",
    recommendation: "Aanbeveling",
    viewProduct: "Bekijk product",
    noMatch: "Geen exacte match gevonden",
    tryAgain: "Probeer opnieuw",
    close: "Sluiten",
    downloadPdf: "Download PDF rapport",
    // Mode selection
    modeSimple: "Simpel",
    modeAdvanced: "Uitgebreid",
    modeSimpleDesc: "4 snelle stappen",
    modeAdvancedDesc: "Alle opties",
    // Advanced sections
    performanceSection: "Performance",
    capacitySection: "Capaciteit",
    vpnSection: "VPN",
    networkSection: "Netwerk",
    featuresSection: "Features",
    interfacesSection: "Interfaces",
    industrialSection: "Industrieel",
    // Advanced labels
    firewallThroughputUdp: "Firewall throughput UDP (Gbps)",
    firewallThroughputImix: "Firewall throughput IMIX (Gbps)",
    ipsThroughputUdp: "IPS throughput UDP (Gbps)",
    ipsThroughputImix: "IPS throughput IMIX (Gbps)",
    vpnThroughputAes: "VPN throughput AES (Gbps)",
    threatPreventionThroughput: "Threat Prevention throughput (Gbps)",
    ngfwUsers: "NGFW gebruikers",
    concurrentConnections: "Gelijktijdige verbindingen",
    newConnectionsPerSecond: "Nieuwe verbindingen/sec",
    vpnIpsecTunnels: "IPSec VPN tunnels",
    vpnSslTunnels: "SSL VPN tunnels",
    maxRules: "Maximaal aantal regels",
    maxStaticRoutes: "Statische routes",
    maxQosQueues: "QoS queues",
    maxLogicalInterfaces: "Logische interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundante voeding",
    redundantDisk: "Redundante disk",
    tpmChipset: "TPM chipset",
    needsCopper1g: "Copper 1G poorten",
    needsCopper2_5g: "Copper 2.5G poorten",
    needsFiber1g: "Fiber 1G poorten",
    needsFiber10g: "Fiber 10G poorten",
    needsFiber25g: "Fiber 25G poorten",
    needsFiber40g: "Fiber 40G poorten",
    dinRail: "DIN Rail montage",
    fanLess: "Fanless design",
    industrialProtocols: "Industriële protocollen",
  },
  en: {
    title: "Firewall Advisor",
    triggerButton: "Which firewall fits me?",
    step1Title: "How many users/connections?",
    step2Title: "VPN connections needed?",
    step3Title: "Performance requirements",
    step4Title: "Extra options",
    users: "Number of users",
    vpnConnections: "VPN connections",
    redundancy: "High Availability (redundancy)",
    redundancyDesc: "Two firewalls in cluster for maximum uptime",
    budget: "Budget indication",
    budgetLow: "Budget-friendly",
    budgetMedium: "Medium",
    budgetHigh: "Premium",
    firewallThroughput: "Firewall throughput (Gbps)",
    ipsThroughput: "IPS throughput (Gbps)",
    next: "Next",
    back: "Back",
    getAdvice: "Get advice",
    loading: "AI analyzing your requirements...",
    recommendation: "Recommendation",
    viewProduct: "View product",
    noMatch: "No exact match found",
    tryAgain: "Try again",
    close: "Close",
    downloadPdf: "Download PDF report",
    // Mode selection
    modeSimple: "Simple",
    modeAdvanced: "Advanced",
    modeSimpleDesc: "4 quick steps",
    modeAdvancedDesc: "All options",
    // Advanced sections
    performanceSection: "Performance",
    capacitySection: "Capacity",
    vpnSection: "VPN",
    networkSection: "Network",
    featuresSection: "Features",
    interfacesSection: "Interfaces",
    industrialSection: "Industrial",
    // Advanced labels
    firewallThroughputUdp: "Firewall throughput UDP (Gbps)",
    firewallThroughputImix: "Firewall throughput IMIX (Gbps)",
    ipsThroughputUdp: "IPS throughput UDP (Gbps)",
    ipsThroughputImix: "IPS throughput IMIX (Gbps)",
    vpnThroughputAes: "VPN throughput AES (Gbps)",
    threatPreventionThroughput: "Threat Prevention throughput (Gbps)",
    ngfwUsers: "NGFW users",
    concurrentConnections: "Concurrent connections",
    newConnectionsPerSecond: "New connections/sec",
    vpnIpsecTunnels: "IPSec VPN tunnels",
    vpnSslTunnels: "SSL VPN tunnels",
    maxRules: "Maximum rules",
    maxStaticRoutes: "Static routes",
    maxQosQueues: "QoS queues",
    maxLogicalInterfaces: "Logical interfaces",
    highAvailability: "High Availability",
    redundantPowerSupply: "Redundant power supply",
    redundantDisk: "Redundant disk",
    tpmChipset: "TPM chipset",
    needsCopper1g: "Copper 1G ports",
    needsCopper2_5g: "Copper 2.5G ports",
    needsFiber1g: "Fiber 1G ports",
    needsFiber10g: "Fiber 10G ports",
    needsFiber25g: "Fiber 25G ports",
    needsFiber40g: "Fiber 40G ports",
    dinRail: "DIN Rail mounting",
    fanLess: "Fanless design",
    industrialProtocols: "Industrial protocols",
  },
  fr: {
    title: "Conseiller Firewall",
    triggerButton: "Quel firewall me convient?",
    step1Title: "Combien d'utilisateurs/connexions?",
    step2Title: "Connexions VPN nécessaires?",
    step3Title: "Exigences de performance",
    step4Title: "Options supplémentaires",
    users: "Nombre d'utilisateurs",
    vpnConnections: "Connexions VPN",
    redundancy: "Haute disponibilité (redondance)",
    redundancyDesc: "Deux firewalls en cluster pour une disponibilité maximale",
    budget: "Indication budgétaire",
    budgetLow: "Économique",
    budgetMedium: "Moyen",
    budgetHigh: "Premium",
    firewallThroughput: "Débit firewall (Gbps)",
    ipsThroughput: "Débit IPS (Gbps)",
    next: "Suivant",
    back: "Retour",
    getAdvice: "Obtenir un conseil",
    loading: "L'IA analyse vos besoins...",
    recommendation: "Recommandation",
    viewProduct: "Voir le produit",
    noMatch: "Aucune correspondance exacte trouvée",
    tryAgain: "Réessayer",
    close: "Fermer",
    downloadPdf: "Télécharger le rapport PDF",
    // Mode selection
    modeSimple: "Simple",
    modeAdvanced: "Avancé",
    modeSimpleDesc: "4 étapes rapides",
    modeAdvancedDesc: "Toutes les options",
    // Advanced sections
    performanceSection: "Performance",
    capacitySection: "Capacité",
    vpnSection: "VPN",
    networkSection: "Réseau",
    featuresSection: "Fonctionnalités",
    interfacesSection: "Interfaces",
    industrialSection: "Industriel",
    // Advanced labels
    firewallThroughputUdp: "Débit firewall UDP (Gbps)",
    firewallThroughputImix: "Débit firewall IMIX (Gbps)",
    ipsThroughputUdp: "Débit IPS UDP (Gbps)",
    ipsThroughputImix: "Débit IPS IMIX (Gbps)",
    vpnThroughputAes: "Débit VPN AES (Gbps)",
    threatPreventionThroughput: "Débit Threat Prevention (Gbps)",
    ngfwUsers: "Utilisateurs NGFW",
    concurrentConnections: "Connexions simultanées",
    newConnectionsPerSecond: "Nouvelles connexions/sec",
    vpnIpsecTunnels: "Tunnels VPN IPSec",
    vpnSslTunnels: "Tunnels VPN SSL",
    maxRules: "Règles maximum",
    maxStaticRoutes: "Routes statiques",
    maxQosQueues: "Files QoS",
    maxLogicalInterfaces: "Interfaces logiques",
    highAvailability: "Haute disponibilité",
    redundantPowerSupply: "Alimentation redondante",
    redundantDisk: "Disque redondant",
    tpmChipset: "Puce TPM",
    needsCopper1g: "Ports Copper 1G",
    needsCopper2_5g: "Ports Copper 2.5G",
    needsFiber1g: "Ports Fiber 1G",
    needsFiber10g: "Ports Fiber 10G",
    needsFiber25g: "Ports Fiber 25G",
    needsFiber40g: "Ports Fiber 40G",
    dinRail: "Montage DIN Rail",
    fanLess: "Design sans ventilateur",
    industrialProtocols: "Protocoles industriels",
  },
};

const defaultAdvancedRequirements: AdvancedFirewallRequirements = {
  firewallThroughputUdp: 0,
  firewallThroughputImix: 0,
  ipsThroughputUdp: 0,
  ipsThroughputImix: 0,
  vpnThroughputAes: 0,
  threatPreventionThroughput: 0,
  users: 0,
  ngfwUsers: 0,
  concurrentConnections: 0,
  newConnectionsPerSecond: 0,
  vpnIpsecTunnels: 0,
  vpnSslTunnels: 0,
  maxRules: 0,
  maxStaticRoutes: 0,
  maxQosQueues: 0,
  maxLogicalInterfaces: 0,
  highAvailability: false,
  redundantPowerSupply: false,
  redundantDisk: false,
  tpmChipset: false,
  needsCopper1g: false,
  needsCopper2_5g: false,
  needsFiber1g: false,
  needsFiber10g: false,
  needsFiber25g: false,
  needsFiber40g: false,
  dinRail: false,
  fanLess: false,
  industrialProtocols: false,
};

interface FirewallAdvisorModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
}

export function FirewallAdvisorModal({ open: controlledOpen, onOpenChange, showTrigger = true }: FirewallAdvisorModalProps) {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage as keyof typeof translations] || translations.nl;
  const { data: products } = useProducts();
  
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;
  
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState<{
    productSlug: string;
    productName: string;
    reason: string;
  } | null>(null);
  
  // Fetch product specs when recommendation is available (moved AFTER recommendation state)
  const { data: productSpecs } = useProductSpecBySlug(recommendation?.productSlug || "");
  
  const [requirements, setRequirements] = useState<FirewallRequirements>({
    users: 50,
    vpnConnections: 10,
    redundancy: false,
    budget: "medium",
    firewallThroughput: 2,
    ipsThroughput: 1,
  });

  const [advancedRequirements, setAdvancedRequirements] = useState<AdvancedFirewallRequirements>(defaultAdvancedRequirements);

  const resetState = () => {
    setStep(1);
    setMode("simple");
    setRecommendation(null);
    setRequirements({
      users: 50,
      vpnConnections: 10,
      redundancy: false,
      budget: "medium",
      firewallThroughput: 2,
      ipsThroughput: 1,
    });
    setAdvancedRequirements(defaultAdvancedRequirements);
  };

  const handleGetAdvice = async () => {
    setLoading(true);
    try {
      const body = mode === "simple" 
        ? { 
            requirements,
            mode: "simple",
            language: currentLanguage,
            availableProducts: products?.map(p => ({
              slug: p.slug,
              name: p.name,
            })) || []
          }
        : {
            advancedRequirements,
            mode: "advanced",
            language: currentLanguage,
            availableProducts: products?.map(p => ({
              slug: p.slug,
              name: p.name,
            })) || []
          };

      const { data, error } = await supabase.functions.invoke("firewall-advisor", {
        body,
      });

      if (error) throw error;

      if (data?.productSlug) {
        setRecommendation(data);
      } else {
        toast.error(t.noMatch);
      }
    } catch (error) {
      console.error("Firewall advisor error:", error);
      toast.error("Er ging iets mis bij het ophalen van advies");
    } finally {
      setLoading(false);
    }
  };

  const languagePrefix = currentLanguage === "nl" ? "" : `/${currentLanguage}`;

  const updateAdvanced = <K extends keyof AdvancedFirewallRequirements>(
    key: K,
    value: AdvancedFirewallRequirements[K]
  ) => {
    setAdvancedRequirements(prev => ({ ...prev, [key]: value }));
  };

  const renderSlider = (
    label: string,
    value: number,
    onChange: (val: number) => void,
    min: number,
    max: number,
    step: number,
    unit?: string
  ) => (
    <div className="space-y-2">
      <div className="flex justify-between">
        <Label className="text-sm">{label}</Label>
        <span className="text-sm text-muted-foreground">{value}{unit ? ` ${unit}` : ''}</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
      />
    </div>
  );

  const renderCheckbox = (
    label: string,
    checked: boolean,
    onChange: (checked: boolean) => void
  ) => (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={label}
        checked={checked}
        onCheckedChange={onChange}
      />
      <Label htmlFor={label} className="text-sm font-normal cursor-pointer">{label}</Label>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetState();
    }}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Sparkles className="h-4 w-4" />
            {t.triggerButton}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-2xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">{t.loading}</p>
          </div>
        ) : recommendation ? (
          <div className="space-y-6 py-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">{t.recommendation}</span>
            </div>
            
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="text-xl font-semibold">{recommendation.productName}</h3>
              <p className="text-muted-foreground">{recommendation.reason}</p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                variant="secondary" 
                onClick={() => {
                  const reqs = mode === "simple" ? requirements : advancedRequirements;
                  generateFirewallPdf(mode, reqs, recommendation, currentLanguage, productSpecs);
                  toast.success(currentLanguage === "nl" ? "PDF wordt gedownload" : currentLanguage === "fr" ? "Téléchargement du PDF" : "Downloading PDF");
                }}
                className="w-full"
              >
                <Download className="mr-2 h-4 w-4" />
                {t.downloadPdf}
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={resetState} className="flex-1">
                  {t.tryAgain}
                </Button>
                <Button asChild className="flex-1">
                  <Link to={`${languagePrefix}/producten/${recommendation.productSlug}`}>
                    {t.viewProduct}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col min-h-0">
            {/* Mode selector */}
            <div className="flex rounded-lg border p-1 gap-1">
              <button
                onClick={() => setMode("simple")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  mode === "simple"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <Zap className="h-4 w-4" />
                <div className="flex flex-col items-start">
                  <span>{t.modeSimple}</span>
                  <span className="text-xs opacity-70">{t.modeSimpleDesc}</span>
                </div>
              </button>
              <button
                onClick={() => setMode("advanced")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  mode === "advanced"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <Settings2 className="h-4 w-4" />
                <div className="flex flex-col items-start">
                  <span>{t.modeAdvanced}</span>
                  <span className="text-xs opacity-70">{t.modeAdvancedDesc}</span>
                </div>
              </button>
            </div>

            {mode === "simple" ? (
              /* Simple mode - step wizard */
              <div className="space-y-6 py-2">
                {/* Step indicators */}
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4].map((s) => (
                    <div
                      key={s}
                      className={`h-2 w-8 rounded-full transition-colors ${
                        s === step ? "bg-primary" : s < step ? "bg-primary/50" : "bg-muted"
                      }`}
                    />
                  ))}
                </div>

                {step === 1 && (
                  <div className="space-y-6">
                    <h3 className="font-medium">{t.step1Title}</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label>{t.users}</Label>
                          <span className="text-sm text-muted-foreground">{requirements.users}</span>
                        </div>
                        <Slider
                          value={[requirements.users]}
                          onValueChange={([value]) => setRequirements({ ...requirements, users: value })}
                          min={10}
                          max={1000}
                          step={10}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <h3 className="font-medium">{t.step2Title}</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label>{t.vpnConnections}</Label>
                          <span className="text-sm text-muted-foreground">{requirements.vpnConnections}</span>
                        </div>
                        <Slider
                          value={[requirements.vpnConnections]}
                          onValueChange={([value]) => setRequirements({ ...requirements, vpnConnections: value })}
                          min={0}
                          max={500}
                          step={5}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6">
                    <h3 className="font-medium">{t.step3Title}</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label>{t.firewallThroughput}</Label>
                          <span className="text-sm text-muted-foreground">{requirements.firewallThroughput} Gbps</span>
                        </div>
                        <Slider
                          value={[requirements.firewallThroughput]}
                          onValueChange={([value]) => setRequirements({ ...requirements, firewallThroughput: value })}
                          min={0.5}
                          max={100}
                          step={0.5}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Label>{t.ipsThroughput}</Label>
                          <span className="text-sm text-muted-foreground">{requirements.ipsThroughput} Gbps</span>
                        </div>
                        <Slider
                          value={[requirements.ipsThroughput]}
                          onValueChange={([value]) => setRequirements({ ...requirements, ipsThroughput: value })}
                          min={0.1}
                          max={50}
                          step={0.1}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-6">
                    <h3 className="font-medium">{t.step4Title}</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>{t.redundancy}</Label>
                          <p className="text-sm text-muted-foreground">{t.redundancyDesc}</p>
                        </div>
                        <Switch
                          checked={requirements.redundancy}
                          onCheckedChange={(checked) => setRequirements({ ...requirements, redundancy: checked })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>{t.budget}</Label>
                        <RadioGroup
                          value={requirements.budget}
                          onValueChange={(value) => setRequirements({ ...requirements, budget: value as "low" | "medium" | "high" })}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="low" id="budget-low" />
                            <Label htmlFor="budget-low" className="font-normal">{t.budgetLow}</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="medium" id="budget-medium" />
                            <Label htmlFor="budget-medium" className="font-normal">{t.budgetMedium}</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="high" id="budget-high" />
                            <Label htmlFor="budget-high" className="font-normal">{t.budgetHigh}</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    disabled={step === 1}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t.back}
                  </Button>
                  
                  {step < 4 ? (
                    <Button onClick={() => setStep(step + 1)}>
                      {t.next}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button onClick={handleGetAdvice}>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t.getAdvice}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              /* Advanced mode - all options */
              <div className="flex flex-col flex-1 overflow-hidden min-h-0">
                <ScrollArea className="flex-1 min-h-0 pr-4" type="auto">
                  <Accordion type="multiple" defaultValue={["performance", "capacity"]} className="space-y-2">
                    {/* Performance Section */}
                    <AccordionItem value="performance" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.performanceSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2 pb-4">
                        {renderSlider(t.firewallThroughputUdp, advancedRequirements.firewallThroughputUdp, (v) => updateAdvanced("firewallThroughputUdp", v), 0, 200, 1)}
                        {renderSlider(t.firewallThroughputImix, advancedRequirements.firewallThroughputImix, (v) => updateAdvanced("firewallThroughputImix", v), 0, 100, 1)}
                        {renderSlider(t.ipsThroughputUdp, advancedRequirements.ipsThroughputUdp, (v) => updateAdvanced("ipsThroughputUdp", v), 0, 100, 1)}
                        {renderSlider(t.ipsThroughputImix, advancedRequirements.ipsThroughputImix, (v) => updateAdvanced("ipsThroughputImix", v), 0, 50, 1)}
                        {renderSlider(t.vpnThroughputAes, advancedRequirements.vpnThroughputAes, (v) => updateAdvanced("vpnThroughputAes", v), 0, 80, 1)}
                        {renderSlider(t.threatPreventionThroughput, advancedRequirements.threatPreventionThroughput, (v) => updateAdvanced("threatPreventionThroughput", v), 0, 50, 1)}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Capacity Section */}
                    <AccordionItem value="capacity" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.capacitySection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2 pb-4">
                        {renderSlider(t.users, advancedRequirements.users, (v) => updateAdvanced("users", v), 0, 10000, 50)}
                        {renderSlider(t.ngfwUsers, advancedRequirements.ngfwUsers, (v) => updateAdvanced("ngfwUsers", v), 0, 5000, 25)}
                        {renderSlider(t.concurrentConnections, advancedRequirements.concurrentConnections, (v) => updateAdvanced("concurrentConnections", v), 0, 50000000, 100000, "K")}
                        {renderSlider(t.newConnectionsPerSecond, advancedRequirements.newConnectionsPerSecond, (v) => updateAdvanced("newConnectionsPerSecond", v), 0, 1000000, 10000)}
                      </AccordionContent>
                    </AccordionItem>

                    {/* VPN Section */}
                    <AccordionItem value="vpn" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.vpnSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2 pb-4">
                        {renderSlider(t.vpnIpsecTunnels, advancedRequirements.vpnIpsecTunnels, (v) => updateAdvanced("vpnIpsecTunnels", v), 0, 20000, 100)}
                        {renderSlider(t.vpnSslTunnels, advancedRequirements.vpnSslTunnels, (v) => updateAdvanced("vpnSslTunnels", v), 0, 10000, 50)}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Network Section */}
                    <AccordionItem value="network" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.networkSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-2 pb-4">
                        {renderSlider(t.maxRules, advancedRequirements.maxRules, (v) => updateAdvanced("maxRules", v), 0, 100000, 1000)}
                        {renderSlider(t.maxStaticRoutes, advancedRequirements.maxStaticRoutes, (v) => updateAdvanced("maxStaticRoutes", v), 0, 50000, 500)}
                        {renderSlider(t.maxQosQueues, advancedRequirements.maxQosQueues, (v) => updateAdvanced("maxQosQueues", v), 0, 2000, 50)}
                        {renderSlider(t.maxLogicalInterfaces, advancedRequirements.maxLogicalInterfaces, (v) => updateAdvanced("maxLogicalInterfaces", v), 0, 2000, 50)}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Features Section */}
                    <AccordionItem value="features" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.featuresSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2 pb-4">
                        {renderCheckbox(t.highAvailability, advancedRequirements.highAvailability, (v) => updateAdvanced("highAvailability", v))}
                        {renderCheckbox(t.redundantPowerSupply, advancedRequirements.redundantPowerSupply, (v) => updateAdvanced("redundantPowerSupply", v))}
                        {renderCheckbox(t.redundantDisk, advancedRequirements.redundantDisk, (v) => updateAdvanced("redundantDisk", v))}
                        {renderCheckbox(t.tpmChipset, advancedRequirements.tpmChipset, (v) => updateAdvanced("tpmChipset", v))}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Interfaces Section */}
                    <AccordionItem value="interfaces" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.interfacesSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2 pb-4">
                        {renderCheckbox(t.needsCopper1g, advancedRequirements.needsCopper1g, (v) => updateAdvanced("needsCopper1g", v))}
                        {renderCheckbox(t.needsCopper2_5g, advancedRequirements.needsCopper2_5g, (v) => updateAdvanced("needsCopper2_5g", v))}
                        {renderCheckbox(t.needsFiber1g, advancedRequirements.needsFiber1g, (v) => updateAdvanced("needsFiber1g", v))}
                        {renderCheckbox(t.needsFiber10g, advancedRequirements.needsFiber10g, (v) => updateAdvanced("needsFiber10g", v))}
                        {renderCheckbox(t.needsFiber25g, advancedRequirements.needsFiber25g, (v) => updateAdvanced("needsFiber25g", v))}
                        {renderCheckbox(t.needsFiber40g, advancedRequirements.needsFiber40g, (v) => updateAdvanced("needsFiber40g", v))}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Industrial Section */}
                    <AccordionItem value="industrial" className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <span className="font-medium">{t.industrialSection}</span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2 pb-4">
                        {renderCheckbox(t.dinRail, advancedRequirements.dinRail, (v) => updateAdvanced("dinRail", v))}
                        {renderCheckbox(t.fanLess, advancedRequirements.fanLess, (v) => updateAdvanced("fanLess", v))}
                        {renderCheckbox(t.industrialProtocols, advancedRequirements.industrialProtocols, (v) => updateAdvanced("industrialProtocols", v))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </ScrollArea>

                {/* Get advice button OUTSIDE ScrollArea - always visible */}
                <div className="pt-4 border-t mt-4 flex-shrink-0">
                  <Button onClick={handleGetAdvice} className="w-full">
                    <Sparkles className="mr-2 h-4 w-4" />
                    {t.getAdvice}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
